import { __rest, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React__default__default, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import PropTypes__default from 'prop-types';
import styles from './tooltip.scss.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import { useVisibilityToggler } from '../../hooks/use-visibility-toggler/use-visibility-toggler.js';
import { TOOLTIP_SIZES, TOOLTIP_POSITIONS, TOOLTIP_ALIGNMENTS, TOOLTIP_WRAP } from './types.js';
import { isOutsideOfContainerEdges, getContainerDimensions, getTargetBoundingClientRect, calculateTooltipPositionZones } from './tooltip-utils.js';
import Balloon from '../balloon/balloon.js';
import { Injecss } from '../../packages/injecss/injecss.js';

// defines base class names for tooltip's target trigger element
var bemTarget = HoneyBem.init('tooltip-target', styles);
var bemTooltip = HoneyBem.init('tooltip', styles);
var Tooltip = function (_a) {
  var id = _a.id,
    active = _a.active,
    children = _a.children,
    _b = _a.closeBtn,
    closeBtn = _b === void 0 ? {} : _b,
    content = _a.content,
    extraClasses = _a.extraClasses,
    _c = _a.hasClose,
    hasClose = _c === void 0 ? false : _c,
    _d = _a.onToggle,
    onToggle = _d === void 0 ? function () {
      return null;
    } : _d,
    _e = _a.openOnHover,
    openOnHover = _e === void 0 ? false : _e,
    position = _a.position,
    alignment = _a.alignment,
    _f = _a.smartPosition,
    smartPosition = _f === void 0 ? false : _f,
    smartPositionRef = _a.smartPositionRef,
    _g = _a.smartPositionOffset,
    smartPositionOffset = _g === void 0 ? {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    } : _g,
    _h = _a.danger,
    danger = _h === void 0 ? false : _h,
    size = _a.size,
    _j = _a.wrapAround,
    wrapAround = _j === void 0 ? 'inline' : _j,
    rest = __rest(_a, ["id", "active", "children", "closeBtn", "content", "extraClasses", "hasClose", "onToggle", "openOnHover", "position", "alignment", "smartPosition", "smartPositionRef", "smartPositionOffset", "danger", "size", "wrapAround"]);
  var tooltipTargetRef = useRef(null);
  var tooltipRef = useRef(null);
  var _k = useState(position),
    smartPositionValue = _k[0],
    setSmartPositionValue = _k[1];
  var _l = useState(alignment),
    smartAlignmentValue = _l[0],
    setSmartAlignmentValue = _l[1];
  var _m = useVisibilityToggler({
      initialActive: active,
      onToggle: onToggle,
      content: tooltipRef.current,
      hoverEvents: openOnHover
    }),
    isActive = _m[0],
    hookEvents = _m[1],
    deactivateTooltip = _m[2];
  var isExpanded = function () {
    return active === undefined ? isActive : active;
  };
  var targetEvents = function (target) {
    var handleClick = function (event) {
      if (target === null || target === void 0 ? void 0 : target.props.onClick) target.props.onClick(event);
      hookEvents.onClick(event);
    };
    var handleKeyDown = function (event) {
      if (target === null || target === void 0 ? void 0 : target.props.onKeyDown) target.props.onKeyDown(event);
      hookEvents.onKeyDown(event);
    };
    return __assign(__assign({}, hookEvents), {
      onKeyDown: handleKeyDown,
      onClick: handleClick
    });
  };
  var targetAria = openOnHover ? {
    'aria-describedby': id
  } : {
    'aria-controls': id,
    'aria-expanded': isExpanded()
  };
  var target = function () {
    if ( /*#__PURE__*/React__default__default.isValidElement(children)) {
      var computedAriaDescribedBy = children.props['aria-describedby'] ? children.props['aria-describedby'].concat(" ".concat(id)) : id;
      targetAria['aria-describedby'] = computedAriaDescribedBy;
      // clone it and attach required events and attributes
      return /*#__PURE__*/React__default__default.cloneElement(children, __assign(__assign({}, targetAria), targetEvents(children)));
    }
    // wrap invalid elements (aka: plain text) with a button
    return /*#__PURE__*/React__default__default.createElement("button", __assign({
      type: "button",
      className: bemTarget.e('button')
    }, targetAria, targetEvents()), children);
  };
  // @todo this is questionable so can be removed if a cleaner way is found
  var isInline = wrapAround === 'inline';
  var Elem = isInline ? 'span' : 'div';
  /**
   * Re-calculates tooltip position value for smart positioning.
   * Runs every time an active state is triggered.
   */
  useLayoutEffect(function () {
    // preventing things breaking if SSR is happening
    // additionally we want smart positioning only happen is position is set to "smart"
    if (smartPosition && typeof window !== 'undefined' && typeof window.document.createElement !== 'undefined') {
      // checking if any edges of the tooltip going beyond the viewport
      // only then we apply "smart" positioning mechanism
      if (isOutsideOfContainerEdges(tooltipRef, window, smartPositionRef === null || smartPositionRef === void 0 ? void 0 : smartPositionRef.current)) {
        var containerRect = getContainerDimensions((smartPositionRef === null || smartPositionRef === void 0 ? void 0 : smartPositionRef.current) || window);
        var tooltipTargetPosition = getTargetBoundingClientRect(tooltipTargetRef.current, smartPositionRef === null || smartPositionRef === void 0 ? void 0 : smartPositionRef.current);
        var zonesSorted = calculateTooltipPositionZones(tooltipTargetPosition, containerRect, smartPositionOffset);
        // getting the zone with max space available
        var maxZoneDirection = zonesSorted[0][0];
        // showing "smart" tooltip in the direction with max space available
        setSmartPositionValue(maxZoneDirection);
        // fine-tuning tooltip position by adjusting the alignment
        var calculatedAlignment = null;
        // smart alignment needs to take 2nd largest zone in consideration, to align towards that
        var secondLargestZone = zonesSorted[1][0];
        var secondLargestZoneSize = zonesSorted[1][1];
        // 3rd largest zone is needed to calculate the threshold to decide if alignment is needed
        var thirdLargestZone = zonesSorted[2][0];
        var thirdLargestZoneSize = zonesSorted[2][1];
        // smart alignment only takes place of alignment zones differ for more than specified number
        // this is a magic number we need, as tooltips never appear centered without this threshold
        var alignmentThreshold = 0.25;
        if (maxZoneDirection === 'left' || maxZoneDirection === 'right') {
          if (secondLargestZone === 'top') {
            calculatedAlignment = 'end';
          }
          if (secondLargestZone === 'bottom') {
            calculatedAlignment = 'start';
          }
          if ((secondLargestZone === 'top' || secondLargestZone === 'bottom') && (thirdLargestZone === 'top' || thirdLargestZone === 'bottom') && secondLargestZoneSize - thirdLargestZoneSize <= secondLargestZoneSize * alignmentThreshold) {
            calculatedAlignment = null;
          }
        }
        if (maxZoneDirection === 'top' || maxZoneDirection === 'bottom') {
          if (secondLargestZone === 'right') {
            calculatedAlignment = 'start';
          }
          if (secondLargestZone === 'left') {
            calculatedAlignment = 'end';
          }
          if ((secondLargestZone === 'left' || secondLargestZone === 'right') && (thirdLargestZone === 'left' || thirdLargestZone === 'right') && secondLargestZoneSize - thirdLargestZoneSize <= secondLargestZoneSize * alignmentThreshold) {
            calculatedAlignment = null;
          }
        }
        // only align when needed, when the difference between the zones is big enough
        if (calculatedAlignment !== null) {
          setSmartAlignmentValue(calculatedAlignment);
        }
      } else {
        // if no tooltip gets cut by the edges of the screen set default position
        setSmartPositionValue(position);
        setSmartAlignmentValue(alignment);
      }
    }
  }, [isActive || active]);
  /**
   * Adds a click listener to the document;
   * when a click on any element that does not contain the tooltip occurs,
   * calls the visibilityToggler `deactivateTooltip` function
   */
  useEffect(function () {
    var handleClickOutside = function (event) {
      if (tooltipTargetRef.current && !tooltipTargetRef.current.contains(event.target) && typeof deactivateTooltip === 'function') {
        deactivateTooltip();
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    // remove document listener when tooltip unmounts
    return function () {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [deactivateTooltip]);
  var handleClose = function (event) {
    // running custom close btn callback if passed
    if (closeBtn === null || closeBtn === void 0 ? void 0 : closeBtn.onClick) {
      closeBtn === null || closeBtn === void 0 ? void 0 : closeBtn.onClick(event);
    }
    deactivateTooltip();
  };
  // generate close button props
  var closeProps = hasClose ? __assign(__assign({}, closeBtn), {
    onClick: handleClose
  }) : null;
  return /*#__PURE__*/React__default__default.createElement(Elem, __assign({
    ref: tooltipTargetRef
  }, rest, {
    className: bemTarget.b({
      inline: isInline
    }, rest.className)
  }), target(), /*#__PURE__*/React__default__default.createElement(Balloon, {
    role: "tooltip",
    id: id,
    innerRef: tooltipRef,
    alignment: smartPosition ? smartAlignmentValue : alignment,
    hidden: active === undefined ? !isActive : !active,
    position: smartPosition ? smartPositionValue : position,
    onMouseLeave: function () {
      return openOnHover && deactivateTooltip();
    },
    onKeyDown: function (event) {
      return event.key === 'Escape' && deactivateTooltip();
    },
    closeProps: closeProps,
    customBem: bemTooltip,
    extraClasses: extraClasses,
    danger: danger,
    size: size
  }, content));
};
Tooltip.displayName = 'Tooltip';
Tooltip.propTypes = {
  id: PropTypes__default.string.isRequired,
  active: PropTypes__default.bool,
  children: PropTypes__default.node.isRequired,
  closeBtn: PropTypes__default.object,
  content: PropTypes__default.node.isRequired,
  hasClose: PropTypes__default.bool,
  danger: PropTypes__default.bool,
  size: PropTypes__default.oneOf(TOOLTIP_SIZES),
  extraClasses: PropTypes__default.string,
  onToggle: PropTypes__default.func,
  openOnHover: PropTypes__default.bool,
  position: PropTypes__default.oneOf(TOOLTIP_POSITIONS),
  alignment: PropTypes__default.oneOf(TOOLTIP_ALIGNMENTS),
  smartPosition: PropTypes__default.bool,
  smartPositionRef: PropTypes__default.oneOfType([PropTypes__default.func, PropTypes__default.shape({
    current: PropTypes__default.object
  })]),
  smartPositionOffset: PropTypes__default.shape({
    top: PropTypes__default.number,
    bottom: PropTypes__default.number,
    left: PropTypes__default.number,
    right: PropTypes__default.number
  }),
  wrapAround: PropTypes__default.oneOf(TOOLTIP_WRAP)
};
var tooltip = Injecss(Tooltip, styles);

export { Tooltip, tooltip as default };
